/**
 * The chip.
 *
 * A chip is one primitive with variants, not one shape per place it appears: an option you can
 * take, a counter you can step, a value already taken. The foundation styles exactly that —
 * `.mdy-chip` carries the box, the variants carry the difference.
 *
 * Naming it here is what lets the variants stay derived. A renderer that wrote `"mdy-chip--counter"`
 * as a string is a renderer deciding what a counter chip is, and the next one spells it differently
 * or forgets the selected modifier — at which point the theme's `.mdy-chip--selected` rule quietly
 * styles nothing. {@link multiselectChipClasses} answers "what classes does this chip carry" once,
 * from the widget's mode and the option's state, and every renderer applies the answer.
 */
import { given } from "./given.js";
import { stateClass } from "./state.js";
/** Canonical class vocabulary for the chip primitive. */
export const MDY_CHIP_CLASSES = Object.freeze({
    /** The chip itself. Every chip carries this, whatever it is for. */
    block: "mdy-chip",
    /** An option in single-select mode: it reserves the room its tick will need. */
    centered: "mdy-chip--centered",
    /** An option that can be taken several times: a label between two step buttons. */
    counter: "mdy-chip--counter",
    /** A value already taken, shown on the control rather than offered in the list. */
    value: "mdy-chip--value",
    /** Taken. The state a theme paints; it applies in either mode. */
    selected: "mdy-chip--selected",
    /** Carries a control for taking the value off again, which changes the room the label has. */
    removable: "mdy-chip--removable",
    /** The tick. Drawn by the theme when a renderer supplies no icon of its own. */
    check: "mdy-chip__check",
    /** The chip's text. */
    label: "mdy-chip__label",
    /** How many times this option has been taken, in counter mode. */
    count: "mdy-chip__count",
    /** A step button — one down, one up. */
    step: "mdy-chip__btn",
    /** The control that takes a chosen value off, on the chip standing for it. */
    remove: "mdy-chip__remove",
    /**
     * The controls that move a chosen value one place, on the chip standing for it.
     *
     * A pointer path that is **not** a drag. WCAG 2.5.7 asks for one independently of any keyboard
     * path: somebody using a pointer who cannot hold and drag — a tremor, a head pointer, a switch —
     * has no way to reorder otherwise, and a keyboard alternative does not discharge it. Drawn only
     * where the field asked to be reorderable, so a set of filters gains no furniture.
     */
    move: "mdy-chip__move",
    /** Wraps a chip a renderer did not draw itself, so a custom option still sits in the grid. */
    wrapper: "mdy-chip-wrapper",
});
/**
 * The classes a chip carries, in order: the primitive, then its variant, then its state.
 *
 * The variant follows the mode, because that is what the difference *is*: an option that can be
 * taken once shows a tick and reserves room for it (`centered`), one that can be taken repeatedly
 * shows a count between two steppers (`counter`). Selection is a state on top of either, never a
 * variant of its own — a theme that styled "selected" twice, once per mode, would drift.
 */
export function multiselectChipClasses(appearance = {}) {
    const { mode = "single", role = "option", selected = false, removable = false } = appearance;
    const classes = [MDY_CHIP_CLASSES.block];
    if (role === "value")
        classes.push(MDY_CHIP_CLASSES.value);
    else
        classes.push(mode === "multi" ? MDY_CHIP_CLASSES.counter : MDY_CHIP_CLASSES.centered);
    // Both are states of the chip, spelled by the shared state vocabulary rather than by this
    // function: `--selected` means the same thing on a chip as it does on an option or a calendar
    // cell, and two places deciding how to spell it is how the two drift apart.
    if (selected)
        classes.push(stateClass(MDY_CHIP_CLASSES.block, "selected"));
    if (removable)
        classes.push(stateClass(MDY_CHIP_CLASSES.block, "removable"));
    return Object.freeze(classes);
}
/**
 * What a button inside a chip is called.
 *
 * The verb alone — "Remove", "One fewer", "Rimuovi" — names the action and not its object, so a strip
 * of eight chips offers eight controls with one name between them. Someone reading the page one
 * control at a time hears "Remove" and has to leave it, find the chip beside it, and come back to
 * know what they would be removing; someone listing the controls hears the same word eight times.
 *
 * **Every button in the chip, not only the one that removes it.** Read from the accessibility tree, a
 * two-chip strip offered `Remove Alfa` and `Remove Beta` beside four steppers called `One fewer` and
 * `One more` — four controls that sound like two, in the same chip that already knew how to say it.
 * And the unnamed pair is the one that destroys: stepping down from one takes the value off.
 *
 * The words stay with the renderer, which is where the language lives. The rule that the object
 * belongs in the name lives here, so all of them compose it the same way.
 */
export function chipActionName(verb, label) {
    const object = label.trim();
    return object === "" ? verb : `${verb} ${object}`;
}
/**
 * Where focus goes when a chip is taken off, named as the chip it should land on.
 *
 * `null` means the strip has nothing left and focus belongs on the control itself.
 *
 * The next chip, or the previous one when the last was removed. Stated rather than left to the
 * browser, because the browser's answer is *whatever now occupies that position in the DOM* — which
 * is the next chip while one exists and nothing at all at the end of the strip, so removing from the
 * middle looked deliberate and removing the last dropped focus to the document. Somebody clearing a
 * strip from the right loses their place on the first press.
 */
export function chipFocusAfterRemoval(order, removed, 
/**
 * Which way the removal was going.
 *
 * `"backward"` — `Backspace` — lands on the chip *before* the one removed, `"forward"` —
 * `Delete` — on the one after. That is what every text field on every platform does, and a strip
 * of chips is close enough to a line of text that a person brings the expectation with them.
 * Absent is forward, which is what a pointer on the chip's own remove control means: there was no
 * direction in the gesture.
 */
direction = "forward") {
    const at = order.indexOf(removed);
    if (at === -1)
        return null;
    const left = order.filter((key) => key !== removed);
    if (left.length === 0)
        return null;
    const wanted = direction === "backward" ? at - 1 : at;
    return left[Math.max(0, Math.min(wanted, left.length - 1))] ?? null;
}
/**
 * What a live region says when a selection changes: the change, and the new total.
 *
 * **The delta, never the list.** A polite region queues rather than replaces, so a full selection
 * announced on every click builds a backlog of stale lists and the person hears a selection several
 * actions out of date. At any size, not only at twelve. The list itself is an on-demand fact and
 * belongs in the field's description, where a reader can ask for it.
 *
 * Empty while the popup is open: the options there carry `aria-selected` and announce natively, so
 * a region firing at the same time makes every toggle speak twice. The row's own removals are the
 * case nothing else speaks for.
 */
/**
 * What the one way back says it is putting back.
 *
 * "Undo" alone is ambiguous once a single reversal covers three acts, so the affordance names the
 * act. A clear has no value to name and says how many it took; a removal and a move name the value,
 * because that is what the person is deciding whether they meant to lose.
 */
export function wayBackSentence(way, templates, labelOf) {
    given("wayBackSentence", "{ act, optionKey, count }", way, ["act", "optionKey", "count"]);
    if (way.act === "clear")
        return templates.cleared.replace("{count}", String(way.count));
    const label = way.optionKey === null ? "" : labelOf(way.optionKey);
    return (way.act === "move" ? templates.moved : templates.removed).replace("{value}", label);
}
/**
 * What the one way back is called, for a reader who meets it as a mark rather than as a sentence.
 *
 * The offer is a control at the field's trailing edge and its mark says only that a way back exists.
 * The name is where the act lives: what will come back, or how many will.
 *
 * Composed from the same three templates the sentence uses rather than from three more of its own.
 * A second set of wordings for one fact drifts the moment either is translated again, and the act is
 * the half that varies — one reversal covers a removal, a move and a clear, and "restore" is wrong
 * for the middle one.
 *
 * With nothing to put back the name is the bare label. The control is on the page at rest, so it is
 * in the accessibility tree at rest, and a button with no name there is announced as "button" —
 * which is what a name is for. The act is the part that is missing, not the control.
 */
export function wayBackActionName(way, templates, labelOf) {
    // Not guarded, and the declaration says why: this door takes `… | null`, so absence is a state it
    // is *for* — "there is no way back" is an answer it gives, not a caller's mistake.
    if (way === null)
        return templates.label;
    return `${templates.label}: ${wayBackSentence(way, templates, labelOf)}`;
}
/**
 * What a live region says when a choice lands: the change, and the new total.
 *
 * The **change**, not the list — a polite region queues rather than replaces, so announcing the whole
 * selection builds a backlog of stale lists and a person hears a selection several acts out of date.
 *
 * Said whether or not the popup is open. It used to be suppressed while open, on the reasoning that
 * the options there announce themselves and a region firing too would speak twice — which holds only
 * for somebody choosing with the keyboard, where focus is on the option. A choice made with a pointer
 * moves no focus and announces nothing at all, so the suppression was silence for exactly the person
 * with no other confirmation. The count is not in the native announcement either way.
 */
export function multiselectAnnouncement(previous, next, words, labelOf) {
    const before = new Set(previous);
    const after = new Set(next);
    // All of them, not the first. One act moves as many values as it moves — a clear takes every one,
    // a way back puts every one of them back — and naming the first of three describes a smaller act
    // than the one that happened. The count beside it stays right either way, which is what makes the
    // shorter sentence sound like an account rather than a fragment: *"Alfa removed, nothing
    // selected"* invites a listener to reconcile the halves themselves, and the reading that comes
    // back is the one where they had only ever chosen Alfa.
    const addedKeys = next.filter((key) => !before.has(key));
    const removedKeys = previous.filter((key) => !after.has(key));
    const added = addedKeys[0];
    const removed = removedKeys[0];
    const moved = addedKeys.length > 0 ? addedKeys.length : removedKeys.length;
    if (added === undefined && removed === undefined)
        return "";
    // Counted rather than listed once more than one moved. The singular templates put the value before
    // a verb that agrees with it, so a list of names dropped into one is ungrammatical in every
    // language that inflects — and twelve names read out for one act a person took knowingly is a list
    // rather than a fact.
    if (moved > 1) {
        const plural = after.size === 0
            ? words.removedManyLast ?? words.removedMany
            : addedKeys.length > 0 ? words.addedMany : words.removedMany;
        if (plural !== undefined) {
            return plural.replace("{moved}", String(moved)).replace("{count}", String(after.size));
        }
    }
    // Emptied, and the sentence carries both halves. Saying only "nothing selected" loses which of
    // twelve went; saying only "Roma removed" loses that the field is now empty. And once the strip is
    // gone there is nothing left in the page to tell them either — a field somebody has just emptied
    // looks exactly like one they never filled. ADR 0148.
    if (after.size === 0) {
        if (removed === undefined || words.removedLast === undefined)
            return words.empty;
        return words.removedLast.replace("{value}", labelOf(removed));
    }
    const template = added !== undefined ? words.added : words.removed;
    return template
        .replace("{value}", labelOf((added ?? removed)))
        .replace("{count}", String(after.size));
}
/**
 * What a live region says when a quantity settles.
 *
 * A quantity that changes says nothing today: the sentence a selection change produces compares the
 * *distinct values* held, and stepping three of something down to two changes none of them. So the
 * only step that spoke was the one that removed the value — a person stepping down heard nothing
 * until what they were counting was gone.
 *
 * **Said on arriving at the smallest quantity, not on leaving it.** Warning at the moment of deletion
 * is too late: the value is already gone and the person is being told rather than asked. Said on
 * arrival, the next step down is a known act.
 */
export function quantityAnnouncement(label, count, words, minimum = 1) {
    const template = count <= minimum ? words.atMinimum : words.settled;
    return template.replace("{value}", label).replace("{count}", String(count));
}
/**
 * A voice that says the value a gesture ended on, rather than every value it passed through.
 *
 * A live region is a queue, and a held arrow key fills it: eleven presses from twelve to one leave
 * eleven polite sentences to be read out after the person has stopped pressing, each describing a
 * state several steps in the past. A `spinbutton` does not have this problem because the platform
 * reads a *value* and coalesces rapid changes itself — so a control that gives up the role takes on
 * the coalescing, which is what this is.
 *
 * `schedule` is injected so a test can settle the voice without waiting: the default is the clock.
 */
export function settledVoice(say, options = {}) {
    const delayMs = options.delayMs ?? 300;
    const schedule = options.schedule ?? ((run, ms) => setTimeout(run, ms));
    const cancel = options.cancel ?? ((handle) => clearTimeout(handle));
    let pending = null;
    return {
        announce(sentence) {
            if (pending !== null)
                cancel(pending);
            pending = schedule(() => { pending = null; say(sentence); }, delayMs);
        },
        stop() {
            if (pending !== null)
                cancel(pending);
            pending = null;
        },
    };
}
/**
 * What a live region says when a chosen value is moved.
 *
 * The `Alt`-plus-arrow way of reordering has no *grabbed* state — nothing is picked up and nothing
 * is put down — so the movement itself is the only thing there is to announce. Unannounced, a
 * reorder is invisible to somebody who cannot see the strip: the value changed and the control said
 * nothing about it.
 */
export function chipMovedAnnouncement(template, label, position, count) {
    return template
        .replace("{value}", label)
        .replace("{position}", String(position))
        .replace("{count}", String(count));
}
/**
 * Where a dragged chip would land, from where the pointer is.
 *
 * Takes the horizontal midpoints of the chips as they are drawn, in order, and answers the index the
 * dragged one would occupy on release. Shared rather than written per renderer for the same reason
 * the dial's angle arithmetic is: three implementations of "which one is the pointer over" is three
 * answers, and the one a person sees is whichever renderer they happen to be using.
 *
 * Reads midpoints rather than edges so the drop follows what the eye does — a chip is "passed" when
 * the pointer is more than halfway across it, not when it clears the far edge.
 *
 * Direction-agnostic: the midpoints arrive in drawing order, so a right-to-left strip answers with
 * the same arithmetic and no renderer has to know which way its own text runs.
 */
export function chipDropIndex(midpoints, clientX, from) {
    if (midpoints.length === 0)
        return from;
    const ascending = (midpoints[midpoints.length - 1] ?? 0) >= (midpoints[0] ?? 0);
    let landed = 0;
    for (const midpoint of midpoints) {
        if (ascending ? clientX > midpoint : clientX < midpoint)
            landed += 1;
    }
    // A chip dragged rightwards passes its own midpoint on the way, which would count it as one place
    // further than the eye reads. Its own slot is not a place it can land on.
    if (landed > from)
        landed -= 1;
    return Math.max(0, Math.min(midpoints.length - 1, landed));
}
/**
 * How far a wheel turn should move a horizontal strip.
 *
 * ADR 0127 lets the chip row scroll on the condition that there is a **mechanism**, not only a cue,
 * for reaching what has scrolled out — and many desktop mice have no horizontal axis at all, so a
 * strip that only answers `deltaX` is a strip a large number of people cannot move. The larger of
 * the two deltas is taken, so a vertical wheel drives it and a trackpad's horizontal gesture still
 * behaves as its owner expects.
 *
 * Answers `0` when the strip has nothing hidden, so the page keeps its own scrolling: a wheel
 * swallowed by a row that had nowhere to go is a page that will not move.
 */
/**
 * A wheel reaches what has scrolled out of a chip strip.
 *
 * ADR 0127 allows the row to scroll only where a mechanism reaches what leaves the viewport, and
 * many desktop mice have no horizontal axis at all — so a vertical wheel over the strip has to move
 * it sideways. The event is cancelled only when the strip actually has something hidden, or the
 * gesture stops scrolling the page for no result.
 */
export function scrollChipStripByWheel(event) {
    const strip = event.currentTarget;
    const delta = chipStripWheelDelta(event.deltaX, event.deltaY, strip.scrollWidth, strip.clientWidth);
    if (delta === 0)
        return;
    event.preventDefault();
    strip.scrollLeft += delta;
}
/**
 * Where a chip's tooltip sits, in the control's own coordinates.
 *
 * Above the strip rather than inside it: the strip clips its overflow, which is the whole reason the
 * name needed revealing. The chip's offset is taken against the strip it scrolls in, so a chip
 * scrolled halfway out is named where it is drawn and not where it began.
 */
export function chipTooltipOffset(chip, strip) {
    return chip.offsetLeft - strip.scrollLeft + strip.offsetLeft;
}
/**
 * How many chips the strip is not showing.
 *
 * A chip counts as hidden when it is not wholly inside the strip's own box — clipped at either edge,
 * because the row scrolls both ways and a chip half off the leading edge is as unreadable as one off
 * the trailing edge. `0` when nothing overflows, which is the case a control must not draw an
 * affordance for.
 *
 * Measured rather than derived from the count: how many fit depends on the labels, the theme's
 * spacing and the width the host gave the field, and a renderer guessing at it would be wrong on the
 * first long name.
 *
 * The chips are found by their own class at any depth, not taken as the strip's immediate children.
 * What the strip holds directly is a layout decision — a row, a grid's row element, one day
 * something else — and counting those counts containers: a single row wider than its box is one
 * child outside the box, so the answer is `1` however many chips are out of sight. A number that is
 * always one is not an imprecise measurement, it is a constant wearing a measurement's clothes, and
 * it reads as correct exactly once.
 */
/**
 * The count, and the mark that reports it, settled against each other.
 *
 * The mark sits in the same row as the strip, so showing it takes width *out of* the strip — and the
 * chip that width covered is then outside a box that was measured before the mark existed. The row
 * reads "29 more" while thirty are cut, which is worse than saying nothing: the mark is the only
 * account a person has of what they cannot see, and one that is short by one is a wrong account
 * delivered confidently.
 *
 * It stayed hidden while the strip had room to spare and appeared the moment a floor was put under
 * the control beside it — the geometry moved, and the off-by-one moved into view with it.
 *
 * Two passes, never more: the second measurement is taken with the mark on the row, and a third
 * could only differ if the number's own width changed the layout again.
 *
 * **The second pass waits for the renderer's own beat.** `apply` may not put the mark on the page
 * itself — a renderer that sets a signal or requests an update returns before the DOM changes, and a
 * measurement taken straight afterwards reads the row as it was, which is the very mistake this
 * exists to correct. A caller that renders synchronously passes nothing and gets both passes now;
 * one that defers passes the beat it already owns — the same shape the command runtime takes, and
 * for the same reason.
 */
export function settleHiddenChipCount(strip, apply, afterRender) {
    const first = hiddenChipCount(strip);
    apply(first);
    const again = () => {
        const second = hiddenChipCount(strip);
        if (second !== first)
            apply(second);
    };
    if (afterRender === undefined) {
        again();
        return hiddenChipCount(strip);
    }
    afterRender(again);
    return first;
}
export function hiddenChipCount(strip) {
    if (strip.scrollWidth <= strip.clientWidth)
        return 0;
    const box = strip.getBoundingClientRect();
    let hidden = 0;
    for (const chip of Array.from(strip.querySelectorAll(`.${MDY_CHIP_CLASSES.block}`))) {
        const at = chip.getBoundingClientRect();
        if (at.left < box.left - 1 || at.right > box.right + 1)
            hidden += 1;
    }
    return hidden;
}
/**
 * Brings the chip that has focus back into the strip, after something changed the strip's width.
 *
 * The browser scrolls a focused element into view once, at the moment focus lands. An affordance
 * that appears on the same beat — the count of what is hidden, a clear-all — takes its width out of
 * the scrollport *afterwards*, and the chip the browser had just brought in is outside again by
 * about the width of the control that appeared. Nothing scrolls a second time on its own.
 *
 * Called after the measurement that may have changed the box, so the rule is: whatever the strip
 * ends up as wide as, the focused chip is inside it.
 */
/**
 * The chosen values as the strip lays them out: distinct, in the order the value has them.
 *
 * The order a strip draws is the order the value holds, not the order the options were declared in —
 * a person who moves a chip has rearranged their own answer, and a strip reading the offer would
 * put it back on the next paint. Repeats collapse to one chip carrying a count, so the sequence is
 * of *distinct* keys.
 *
 * Derived here rather than in each renderer because the key is the contract's: it is what
 * `keyFor` produced, which for an object value is a structural key and not `String(value)`. A
 * renderer deriving its own has a second key function, and the two agree on strings and part ways
 * on everything else — every object collapsing to one entry, so a strip of five chips reorders as
 * if it held one.
 */
export function chosenKeyOrder(state) {
    given("chosenKeyOrder", "{ counts }", state, ["counts"]);
    return [...state.counts.keys()];
}
/**
 * The element carrying a key, found by comparing rather than by selecting.
 *
 * A key is derived from a value, and a value is whatever the document put in it. Inside an attribute
 * selector it stops being data and becomes syntax: a quote closes the selector and the browser
 * throws `SyntaxError`, which for a structural key — the shape an object value takes — is every
 * time. The gesture does not misbehave, it raises, and it takes the surrounding handler with it.
 *
 * Escaping would work and is not what this does. `CSS.escape` is a browser global this package must
 * not require — it computes in processes with no DOM — and a hand-rolled escape for attribute values
 * is a second set of rules to keep correct. Comparing needs neither: the key is read back from the
 * dataset as the string it was written as.
 */
export function elementByDataKey(root, attribute, key) {
    const dataProperty = attribute === "key" ? "key" : "optionKey";
    for (const candidate of Array.from(root.querySelectorAll(`[data-${attribute}]`))) {
        if (candidate.dataset[dataProperty] === key)
            return candidate;
    }
    return null;
}
export function keepFocusedChipInView(strip) {
    const focused = strip.ownerDocument.activeElement;
    if (!(focused instanceof HTMLElement) || !strip.contains(focused))
        return;
    const chip = focused.closest(".mdy-chip");
    if (!(chip instanceof HTMLElement))
        return;
    const box = strip.getBoundingClientRect();
    const at = chip.getBoundingClientRect();
    if (at.left >= box.left - 1 && at.right <= box.right + 1)
        return;
    // `nearest` in both axes: the strip is the only thing that should move, and a chip that is already
    // vertically where it belongs must not drag the page to it.
    chip.scrollIntoView({ block: "nearest", inline: "nearest" });
}
export function chipStripWheelDelta(deltaX, deltaY, scrollWidth, clientWidth) {
    if (scrollWidth <= clientWidth)
        return 0;
    return Math.abs(deltaX) > Math.abs(deltaY) ? deltaX : deltaY;
}
export function beginChipReorder(press, chip, options) {
    given("beginChipReorder", "{ button, clientX }", press, ["button", "clientX"]);
    if (press.button !== 0)
        return;
    const view = chip.ownerDocument;
    if (!view)
        return;
    const travel = options.threshold ?? MDY_CHIP_DRAG_THRESHOLD;
    const startX = press.clientX;
    let dragging = false;
    const onMove = (move) => {
        if (!dragging && Math.abs(move.clientX - startX) < travel)
            return;
        dragging = true;
        chip.classList.add(options.draggingClass);
    };
    const onUp = (up) => {
        detach();
        if (!dragging)
            return;
        view.addEventListener("click", (click) => { click.stopPropagation(); click.preventDefault(); }, { capture: true, once: true });
        const from = options.from();
        const to = chipDropIndex(options.midpoints(), up.clientX, from);
        if (to !== from)
            options.onDrop(to);
    };
    const onCancel = () => detach();
    function detach() {
        view.removeEventListener("pointermove", onMove);
        view.removeEventListener("pointerup", onUp);
        view.removeEventListener("pointercancel", onCancel);
        chip.classList.remove(options.draggingClass);
    }
    view.addEventListener("pointermove", onMove);
    view.addEventListener("pointerup", onUp);
    view.addEventListener("pointercancel", onCancel);
}
/**
 * How far a press travels before it is a drag.
 *
 * Published because it is the number that decides whether a chip's own buttons still work: too small
 * and a steady finger reorders the strip instead of pressing what it is on, too large and a drag has
 * to be exaggerated before anything moves.
 */
export const MDY_CHIP_DRAG_THRESHOLD = 6;
